import { UserType } from '@/constants';
import { createScopedPersistStorage, getMenuRecentPersistKey } from '@/helpers/persistScope';
import { useUserStore } from '@/stores/user';
import { IBreadcrumb, IMenuItem, INavMenu, INavMenuLeaf } from '@/types';

const RECENT_MENU_LIMIT = 10;

function collectMenuLeaves(items: IMenuItem[], parentTitle = ''): INavMenuLeaf[] {
    const leaves: INavMenuLeaf[] = [];

    for (const item of items) {
        if (item.subItems?.length) {
            leaves.push(...collectMenuLeaves(item.subItems, item.title));
        } else if (item.link) {
            leaves.push({
                parentTitle,
                title: item.title,
                link: item.link,
                params: item.params,
            });
        }
    }

    return leaves;
}

export const useMenuStore = defineStore(
    'menu',
    () => {
        const userStore = useUserStore();

        const navMenuTurnover = ref<INavMenu>({
            title: 'turnover',
            icon: mdiSoccer,
            subItems: [
                { title: 'turnoverSelection', link: 'turnoverSelection' },
                { title: 'turnoverEvent', link: 'turnoverEvent' },
            ],
        });

        const navMenuGame = ref<INavMenu>({
            title: 'events',
            icon: mdiSoccer,
            subItems: [
                { title: 'pregame', link: 'pregame' },
                { title: 'live', link: 'live' },
            ],
        });

        const adminNavMenuAdmin = ref<INavMenu>({
            title: 'admin',
            icon: mdiSoccer,
            subItems: [
                { title: 'toolUsers', link: 'toolUsers' },
                { title: 'toolUserDetails', link: 'toolUserDetails' },
                { title: 'toolUserDocuments', link: 'toolUserDocuments' },
                { title: 'accounts', link: 'accounts' },
                { title: 'bets', link: 'bets' },
                { title: 'betSettlement', link: 'betSettlement' },
                { title: 'leagueDailyCoverage', link: 'leagueDailyCoverage' },
                { title: 'activity', link: 'activity' },
                { title: 'orders', link: 'orders' },
                { title: 'notifications', link: 'notifications' },
                { title: 'wlTransaction', link: 'wlTransaction' },
                { title: 'mappingProblem', link: 'mappingProblem' },
                { title: 'bots', link: 'bots' },
                { title: 'botUsageStats', link: 'botUsageStats' },
                { title: 'transfer', link: 'transfer' },
            ],
        });

        const navMenuSystem = computed<INavMenu>(() => ({
            title: 'system',
            icon: mdiSoccer,
            subItems: [
                { title: 'bookmaker', link: 'bookmaker' },
                { title: 'monitoring', link: 'monitoring' },
                { title: 'team', link: 'team' },
                { title: 'customTeam', link: 'customTeam' },
                { title: 'proxy', link: 'proxy' },
                { title: 'alerts', link: 'alerts' },
                { title: 'leagues', link: 'leagues' },
                { title: 'customLeagues', link: 'customLeagues' },
                { title: 'loggedPlayers', link: 'loggedPlayers' },
                ...(userStore.isRoot() ? [{ title: 'loggedAgentsAdmins', link: 'loggedAgentsAdmins' }] : []),
                ...(userStore.isRoot() ? [{ title: 'audit', link: 'audit' }] : []),
                { title: 'exchangeRate', link: 'currencyExchange' },
                ...(userStore.isRoot() ? [{ title: 'pageVisitHistory', link: 'pageVisitHistory' }] : []),
            ],
        }));

        const navMenuAccountingAdmin = ref<INavMenu>({
            title: 'accounting',
            icon: mdiSoccer,
            subItems: [
                { title: 'bookBalance', link: 'bookBalance' },
                { title: 'userBalance', link: 'userBalance' },
                { title: 'wlBalance', link: 'wlBalance' },
                { title: 'customerPl', link: 'customerPl' },
            ],
        });

        const navMenuAccountingCompany = ref<INavMenu>({
            title: 'accounting',
            icon: mdiSoccer,
            subItems: [
                { title: 'bookBalance', link: 'bookBalance' },
                { title: 'wlBalance', link: 'wlBalance' },
                { title: 'customerPl', link: 'customerPl' },
            ],
        });

        const agentNavMenuAdmin = ref<INavMenu>({
            title: 'admin',
            icon: mdiSoccer,
            subItems: [
                { title: 'bets', link: 'bets' },
                // { title: 'report', link: 'report' },
            ],
        });

        const companyNavMenuAdmin = ref<INavMenu>({
            title: 'admin',
            icon: mdiSoccer,
            subItems: [
                { title: 'toolUsers', link: 'toolUsers' },
                { title: 'toolUserDetails', link: 'toolUserDetails' },
                { title: 'toolUserDocuments', link: 'toolUserDocuments' },
                { title: 'bets', link: 'bets' },
                { title: 'leagueDailyCoverage', link: 'leagueDailyCoverage' },
                { title: 'orders', link: 'orders' },
                { title: 'transfer', link: 'transfer' },
            ],
        });

        const withMenu = ref<boolean>(false);
        const userMenu = ref<boolean>(false);
        const mobileMenuDrawer = ref<boolean>(false);
        const breadcrumbs = ref<IBreadcrumb[]>([]);
        const recentMenuLinks = ref<string[]>([]);

        const adminNavMenuItems = computed((): IMenuItem[] => [
            navMenuTurnover.value,
            navMenuGame.value,
            navMenuAccountingAdmin.value,
            adminNavMenuAdmin.value,
            navMenuSystem.value,
        ]);
        const companyNavMenuItems = computed((): IMenuItem[] => [navMenuTurnover.value, navMenuAccountingCompany.value, companyNavMenuAdmin.value]);
        const agentNavMenuItems = computed((): IMenuItem[] => [agentNavMenuAdmin.value]);

        const navMenuItems = computed(() => {
            switch (userStore.type) {
                case UserType.Root:
                case UserType.Admin:
                    return adminNavMenuItems.value;
                case UserType.Company:
                    return companyNavMenuItems.value;
                case UserType.Agent:
                case UserType.AgentView:
                case UserType.AgentPlatform:
                    return agentNavMenuItems.value;

                default:
                    return [];
            }
        });

        const navMenuLeaves = computed((): INavMenuLeaf[] => collectMenuLeaves(navMenuItems.value));

        const recentMenuLeaves = computed((): INavMenuLeaf[] => {
            const byLink = new Map(navMenuLeaves.value.map((item) => [item.link, item]));
            return recentMenuLinks.value.map((link) => byLink.get(link)).filter((item): item is INavMenuLeaf => item != null);
        });

        function setBreadcrumbs(value: IBreadcrumb[]) {
            // Replace with actual translation in component
            breadcrumbs.value = value.map((item) => ({
                title: `menu.${item.text}`,
                disabled: item.disabled ?? false,
            }));
        }

        function clearRecentMenuLinks() {
            recentMenuLinks.value = [];
        }

        function recordRecentMenuLink(link: string) {
            const validLinks = new Set(navMenuLeaves.value.map((item) => item.link));
            if (!validLinks.has(link)) return;

            recentMenuLinks.value = [link, ...recentMenuLinks.value.filter((item) => item !== link && validLinks.has(item))].slice(0, RECENT_MENU_LIMIT);
        }

        return {
            navMenuItems,
            navMenuLeaves,
            recentMenuLeaves,
            recordRecentMenuLink,
            clearRecentMenuLinks,
            setBreadcrumbs,
            breadcrumbs,
            withMenu,
            userMenu,
            mobileMenuDrawer,
            recentMenuLinks,
        };
    },
    {
        persist: {
            pick: ['recentMenuLinks'],
            storage: createScopedPersistStorage(getMenuRecentPersistKey),
        },
    }
);

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useMenuStore, import.meta.hot));
}
